/**
 * Helpers for walking wrapped error chains (Drizzle, pg, tRPC, etc.)
 * without using `any`.
 */
export interface ErrorChainNode {
  message?: string;
  error?: string;
  code?: string;
  stack?: string;
  cause?: unknown;
  driverError?: unknown;
  originalError?: unknown;
}

export function asErrorChainNode(error: unknown): ErrorChainNode {
  return (typeof error === "object" && error !== null ? error : {}) as ErrorChainNode;
}

function readApiErrorText(record: Record<string, unknown>): string | undefined {
  if (typeof record.message === "string" && record.message.length > 0) {
    return record.message;
  }
  if (typeof record.error === "string" && record.error.length > 0) {
    return record.error;
  }
  if (typeof record.error === "object" && record.error !== null) {
    const nested = record.error as Record<string, unknown>;
    if (typeof nested.message === "string" && nested.message.length > 0) {
      return nested.message;
    }
  }
  return undefined;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error == null) return "Unknown error";
  if (typeof error === "object") {
    const apiText = readApiErrorText(error as Record<string, unknown>);
    if (apiText) return apiText;
    // No readable API fields — return empty so `getErrorMessage(x) || fallback` works.
    return "";
  }
  return String(error);
}

export function getErrorCode(error: unknown): string | undefined {
  const node = asErrorChainNode(error);
  return typeof node.code === "string" ? node.code : undefined;
}
